import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import Link from 'components/shared/link';
import Heading from 'components/shared/heading';

import './cards-with-image.scss';

/**
 * Gatsby image markup uses inline styles that differ between SSR and client (e.g. opacity fade-in).
 * Recursively mark the subtree so React skips attribute diffing and avoids hydration warnings.
 */
function withSuppressHydrationWarningTree(node) {
  if (!React.isValidElement(node)) {
    return node;
  }

  const processedChildren = React.Children.map(node.props.children, (child) =>
    withSuppressHydrationWarningTree(child)
  );

  return React.cloneElement(node, { suppressHydrationWarning: true }, processedChildren);
}

const CardsWithImage = ({
  title,
  description = null,
  items,
  background = 'gray',
  blockWidth = 100,
  children = null,
}) => {
  const childrenArray = useMemo(
    () => React.Children.toArray(children).filter((child) => React.isValidElement(child)),
    [children]
  );

  const itemsInnerClassName = useMemo(
    () =>
      [
        'cards-with-image__items-inner',
        items?.length === 3 && 'cards-with-image__items-inner-column-3',
        items?.length === 4 && 'cards-with-image__items-inner-column-4',
      ]
        .filter(Boolean)
        .join(' '),
    [items]
  );

  return (
    <section
      className={`cards-with-image cards-with-image_background_${background} use-intersection-observer`}
    >
      <div className="container cards-with-image-inner">
        <div className="cards-with-image-head">
          <Heading className="cards-with-image__title" tag="h2" size="xl" innerHTML={title} />
          {description && <p className="cards-with-image__description">{description}</p>}
        </div>
        <div className="cards-with-image__items-wrapper" style={{ width: `${blockWidth}%` }}>
          <div className={itemsInnerClassName}>
            {items?.map(({ title, description, url, buttonLabel, buttonStyle }, index) => (
              <Link
                className="cards-with-image__item"
                to={url}
                key={index}
                suppressHydrationWarning
              >
                <div className="cards-with-image__item-image-wrapper">
                  <div className="cards-with-image__item-image">
                    {childrenArray[index]
                      ? withSuppressHydrationWarningTree(childrenArray[index])
                      : null}
                  </div>
                </div>
                <div className="cards-with-image__item-content">
                  <div>
                    {title && (
                      <Heading
                        className="cards-with-image__item-title"
                        tag="h3"
                        size="md"
                        innerHTML={title}
                      />
                    )}
                    {description && (
                      <p className="cards-with-image__item-description">{description}</p>
                    )}
                  </div>
                  <div className="cards-with-image__item-footer">
                    <span
                      className={`cards-with-image__item-read-more${
                        buttonStyle ? ` cards-with-image__item-read-more_${buttonStyle}` : ''
                      }`}
                    >
                      {buttonLabel || 'Read More'}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

CardsWithImage.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      description: PropTypes.string,
      image: PropTypes.shape({
        id: PropTypes.number,
      }),
      url: PropTypes.string.isRequired,
      buttonLabel: PropTypes.string,
      buttonStyle: PropTypes.oneOf(['primary', 'default']),
    })
  ).isRequired,
  background: PropTypes.oneOf(['white', 'gray', 'darkblue']),
  blockWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  children: PropTypes.node,
};

export default CardsWithImage;
